<script lang="ts">
  import type { TraceNode, EventStatus } from '../types/debugger';
  import TraceSummary from './TraceSummary.svelte';

  let { node, depth = 0 }: { node: TraceNode; depth?: number } = $props();

  // svelte-ignore state_referenced_locally — intentional: capture initial depth for expand state
  let expanded = $state(depth < 2);

  const typeIcons: Record<TraceNode['type'], string> = {
    lambda: 'λ', agent: '🤖', tool: '🔧', llm: '🧠', sqs: '📨', ddb: '🗄️',
  };

  const statusColors: Record<EventStatus, string> = {
    SUCCESS: 'bg-success', FAILED: 'bg-error', IN_PROGRESS: 'bg-accent animate-pulse-orange',
    PENDING: 'bg-text-muted', SKIPPED: 'bg-warning',
  };

  let hasChildren = $derived(!!node.children?.length);

  function formatDuration(ms: number): string {
    return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
  }
</script>

<div class="relative" style="margin-left: {depth * 20}px">
  {#if depth > 0}
    <div class="absolute -left-3 top-0 bottom-0 w-px bg-border-light" style="animation: draw-line 0.4s ease-out forwards;"></div>
    <div class="absolute -left-3 top-4 w-3 h-px bg-border-light"></div>
  {/if}

  <button
    onclick={() => { if (hasChildren) expanded = !expanded; }}
    class="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors
      {hasChildren ? 'hover:bg-bg-hover cursor-pointer' : 'cursor-default'}"
  >
    {#if hasChildren}
      <svg
        class="w-3 h-3 text-text-muted transition-transform duration-150 shrink-0 {expanded ? '' : '-rotate-90'}"
        fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    {:else}
      <div class="w-3"></div>
    {/if}

    <span class="text-sm shrink-0" title={node.type}>{typeIcons[node.type]}</span>
    <span class="text-xs font-medium text-text-primary truncate">{node.name}</span>
    <span class="text-[10px] text-text-muted ml-auto shrink-0">{formatDuration(node.duration)}</span>
    <div class="w-1.5 h-1.5 rounded-full shrink-0 {statusColors[node.status]}"></div>
  </button>

  {#if node.error}
    <div class="ml-8 mt-1 px-2 py-1 rounded bg-error/10 border border-error/20 text-[10px] text-error font-mono">
      {node.error}
    </div>
  {/if}

  {#if hasChildren && expanded}
    <div class="relative mt-0.5 animate-fade-in-up">
      {#each node.children! as child (child.id)}
        <TraceSummary node={child} depth={depth + 1} />
      {/each}
    </div>
  {/if}
</div>
